import type { DocNode as ADF } from '@atlaskit/adf-schema';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { CommentRoleType } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { Action } from '@atlassian/react-sweet-state';
import type {
	ObjectActivityTimelineState,
	ContainerProps,
} from '../../../../common/types/store.tsx';
import {
	isUpdateObjectCommentServiceResponseData,
	type ObjectCommentTimelineEvent,
	OBJECT_COMMENT_EVENT,
} from '../../../../common/types/timeline-event.tsx';
import { editObjectComment } from '../../../../services/object-activity/index.tsx';
import { updateCommentStateById } from '../update-comment-state-by-id/index.tsx';

const getCommentById = (
	commentId: string,
	storeState: Readonly<ObjectActivityTimelineState>,
): ObjectCommentTimelineEvent => {
	const comment = (storeState.timelineEvents ?? []).find(
		(e) => e.type === OBJECT_COMMENT_EVENT && e.data.id === commentId,
	);

	// Have to check `.type` again, otherwise flow complains
	if (!comment || comment.type !== OBJECT_COMMENT_EVENT) {
		throw new Error("Attempting to update comment that doesn't exist");
	}

	return comment;
};

type ActionPayload = {
	role: CommentRoleType;
	commentId: string;
	comment: ADF;
	onFailure: () => void;
};

export const updateObjectComment =
	({
		role,
		comment,
		onFailure,
		commentId,
	}: ActionPayload): Action<ObjectActivityTimelineState, ContainerProps> =>
	async ({ dispatch, getState }, { workspaceId, objectId, createAnalyticsEvent }) => {
		try {
			// Store the original comment in case of error
			const originalComment = getCommentById(commentId, getState());

			// Optimistically update the existing comment in the store
			dispatch(
				updateCommentStateById(commentId, {
					isSaving: true,
					comment,
					role,
				}),
			);

			const serviceResponse = await editObjectComment({
				workspaceId,
				objectId,
				role,
				commentId,
				comment,
			});

			if (isUpdateObjectCommentServiceResponseData(serviceResponse)) {
				const { data } = serviceResponse.data;

				// Overwrite the optimistic comment with the server response
				dispatch(
					updateCommentStateById(commentId, {
						...data,
						isSaving: false,
					}),
				);

				fireTrackAnalytics(createAnalyticsEvent({}), 'updateObjectComment succeeded');
			} else if (serviceResponse.error) {
				// Overwrite the optimistically updated comment with the original unedited comment
				dispatch(
					updateCommentStateById(commentId, {
						...originalComment.data,
						isSaving: false,
					}),
				);

				throw serviceResponse.rawError;
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			onFailure();

			fireCmdbErrorAnalytics({
				error,
				meta: {
					id: 'updateObjectComment',
					packageName: 'jiraServicedeskInsightObjectActivityTimelineStore',
					teamName: 'falcons',
				},
				attributes: {
					errorMessage: error.message,
				},
			});
		}
	};
